import React, { useState } from "react";
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  ScrollView,
} from "react-native";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { StackActions, useNavigation } from "@react-navigation/native";
import { navigateToScreen } from "../../../../utility/NavigationHelper";
import { AppImages } from "../../../../utility/AppImages";
import { CommonButton } from "../../../../common/CommonButton";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import { AppColors } from "../../../../utility/AppColors";
import { updateEvent } from "../../../../services/eventsAPI";
import { toast } from "react-toastify";
import { AppScreens } from "../../../Utility/AppScreens";

interface ChangesReadyToSendModalProps {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  eventID: string;
  onClose: () => void;
  updatedEventDetails: any;
  previousEventDetails: any;
  accessToken: string;
}

const fieldsToCheck = [
  "title",
  "startDateTime",
  "gameTitle",
  "gamerID",
  "device",
  "eventImageUrl",
  "description",
];

const ChangesReadyToSendModal: React.FC<ChangesReadyToSendModalProps> = ({
  showModal = false,
  setShowModal,
  eventID,
  onClose,
  updatedEventDetails,
  previousEventDetails,
  accessToken,
}) => {
  console.log("Updated:", updatedEventDetails);
  console.log("Previous:", previousEventDetails);
  const navigation = useNavigation();
  const styles = useStyles();
  const deviceSize = useDeviceSize();
  const [isLoading, setIsLoading] = useState(false);

  const onCloseClick = () => {
    setShowModal(false);
    onClose();
  };

  const fieldDisplayNames = {
    title: "Event Title",
    startDateTime: "Start Date & Time",
    gameTitle: "Game Title",
    gamerID: "Gamer ID",
    device: "Device",
    eventImageUrl: "Event Image",
    description: "Description",
  };

  const getPreviousFieldValue = (field: string) => {
    switch (field) {
      case "title":
        return previousEventDetails?.eventTitle;
      case "startDateTime":
        return previousEventDetails?.eventStartTime;
      case "gamerID":
        return previousEventDetails?.gamerID?.organiser;
      default:
        return previousEventDetails?.[field];
    }
  };

  const getUpdatedFieldValue = (field: string) => {
    if (field === "gamerID" && updatedEventDetails[field]) {
      return updatedEventDetails[field].organiser;
    }
    return updatedEventDetails[field];
  };

  const formatValue = (field: string, value: any) => {
    if (field === "eventImageUrl" && value && typeof value === "string") {
      return (
        <Image
          source={{ uri: value }}
          style={{ width: 100, height: 50, resizeMode: "contain" }}
        />
      );
    }
    if (field === "startDateTime" && value) {
      return new Date(value).toLocaleString();
    }
    if (field === "gamerID" && value !== undefined && value !== null) {
      return value; // Already extracted as organiser string
    }
    return value !== undefined && value !== null ? value : "Not Set";
  };

  const changedFields =
    previousEventDetails && updatedEventDetails
      ? fieldsToCheck.filter((field) => {
          const prevValue = getPreviousFieldValue(field);
          const newValue = getUpdatedFieldValue(field);
          return JSON.stringify(newValue) !== JSON.stringify(prevValue);
        })
      : [];

  const getUpdatedFieldsOnly = () => {
    const updatedFields: any = {};
    changedFields.forEach((field) => {
      if (field === "title") {
        updatedFields.eventTitle = updatedEventDetails[field];
      } else if (field === "startDateTime") {
        updatedFields.eventStartTime = updatedEventDetails[field];
      } else if (field === "gamerID") {
        updatedFields.gamerID = updatedEventDetails[field];
      } else {
        updatedFields[field] = updatedEventDetails[field];
      }
    });
    return updatedFields;
  };

  const handleSendChanges = async () => {
    setIsLoading(true);

    const updatedFields = getUpdatedFieldsOnly();

    const fieldsToCheck = [
      "title",
      "startDateTime",
      "gameTitle",
      "gamerID",
      "device",
      "eventImageUrl",
      "description",
    ];

    const mappedDetails = Object.fromEntries(
      fieldsToCheck.map((field) => {
        let updatedKey = field;

        if (field === "title") updatedKey = "eventTitle";
        if (field === "startDateTime") updatedKey = "eventStartTime";

        return [
          field,
          updatedFields[updatedKey] ?? previousEventDetails[updatedKey],
        ];
      })
    );

    console.log(mappedDetails);

    const bodyParams = {
      accessToken,
      eventID,
      newDetails: mappedDetails,
    };

    try {
      const response = await updateEvent(bodyParams);
      if (response.statusCode === 200 || response.statusCode === 201) {
        toast.success(response.body?.message || "Changes sent for approval");
        navigateToScreen(navigation, AppScreens.OnGoingEvents, {
            eventID: eventID,
          });
      } else {
        toast.error(response.body?.error || "Failed to send changes");
      }
    } catch (error) {
      toast.error(error.message || "Error sending changes");
    } finally {
      setIsLoading(false);
      setShowModal(false);
    }
  };

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={showModal}
      onRequestClose={onCloseClick}
    >
      <View style={styles.container}>
        <View style={[styles.modalView, { marginVertical: 20 }]}>
          <ScrollView
            contentContainerStyle={[
              {
                flexGrow: 1,
                alignItems: "center",
                justifyContent: "center",
                alignSelf: "center",
                width: "100%",
              },
            ]}
            style={{ width: "100%" }}
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.header}>
              <Text style={styles.headerText}>Changes Ready to Send</Text>
              <TouchableOpacity onPress={onCloseClick} disabled={isLoading}>
                <Image
                  source={AppImages.icCloseWhite}
                  style={{
                    height:
                      deviceSize === DEVICE_SIZES.SM ||
                      deviceSize === DEVICE_SIZES.XS
                        ? 20
                        : 25,
                    width:
                      deviceSize === DEVICE_SIZES.SM ||
                      deviceSize === DEVICE_SIZES.XS
                        ? 20
                        : 25,
                  }}
                />
              </TouchableOpacity>
            </View>

            <View style={styles.separator} />

            <View style={styles.small}>
              <View style={styles.content}>
                <Image source={AppImages.icChangesLogo} style={styles.logo} />
                <Text style={styles.description}>
                  You’ve updated the event details.
                </Text>
                <Text style={styles.description}>
                  Review the changes below and send them for approval.
                </Text>
              </View>

              {changedFields.length > 0 && (
                <View style={styles.summaryContainer}>
                  <View style={styles.summaryHeaderContainer}>
                    <Text style={styles.summaryHeader}>Change Summary</Text>
                  </View>
                  <View style={styles.separator} />

                  <View style={styles.summaryRow}>
                    {changedFields.map((field) => (
                      <View key={field} style={styles.summaryBox}>
                        <Text style={styles.summaryTitle}>
                          {fieldDisplayNames[field]}
                        </Text>
                        <View style={styles.row}>
                          <Text
                            style={styles.summaryValue}
                            numberOfLines={2}
                            ellipsizeMode="tail"
                          >
                            {formatValue(field, getPreviousFieldValue(field))}
                          </Text>
                          <Image
                            source={AppImages.icArrow}
                            style={styles.arrowIcon}
                          />
                          <Text
                            style={styles.summaryValue}
                            numberOfLines={2}
                            ellipsizeMode="tail"
                          >
                            {formatValue(field, getUpdatedFieldValue(field))}
                          </Text>
                        </View>
                      </View>
                    ))}
                  </View>
                </View>
              )}
            </View>

            <View style={styles.separator} />

            <View style={styles.buttonContainer}>
              <CommonButton
                title="Cancel"
                onTap={onCloseClick}
                buttonStyle={styles.cancelButton}
                textStyle={styles.cancelText}
                disabled={isLoading}
              />
              {changedFields.length === 0 ? (
                <CommonButton
                  buttonStyle={[styles.submitButton]}
                  textStyle={styles.submitText}
                  disabled={true}
                  title="No changes to send"
                />
              ) : (
                <CommonButton
                  title={isLoading ? "Sending..." : "Send Changes for Approval"}
                  onTap={handleSendChanges}
                  buttonStyle={[
                    styles.submitButton,
                    isLoading && styles.disabledButton,
                  ]}
                  textStyle={styles.submitText}
                  disabled={isLoading}
                  leftIcon={
                    isLoading ? (
                      <ActivityIndicator size="small" color={AppColors.white} />
                    ) : null
                  }
                />
              )}
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const useStyles = CreateResponsiveStyle(
  {
    container: {
      flex: 1,
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      justifyContent: "center",
      alignItems: "center",
    },
    disabledButton: {
      backgroundColor: "gray",
    },
    modalView: {
      width: "75%",
      backgroundColor: "#18151B",
      borderRadius: 12,
      padding: 20,
      alignItems: "center",
    },
    small: {
      backgroundColor: AppColors.purple_453B50,
      padding: 16,
      borderRadius: 24,
      marginBottom: 20,
      marginTop: 20,
      width: "100%",
    },
    header: {
      flexDirection: "row",
      width: "100%",
      justifyContent: "space-between",
      alignItems: "center",
    },
    headerText: {
      ...AppFontStyle.BOLD_ANYBODY_24,
      color: AppColors.white,
    },
    closeIcon: {
      height: 28,
      width: 28,
    },
    separator: {
      width: "100%",
      height: 2,
      backgroundColor: "#333",
      marginVertical: 8,
    },
    content: {
      alignItems: "center",
      marginVertical: 15,
    },
    logo: {
      height: 145,
      width: 145,
      marginBottom: 10,
    },
    description: {
      ...AppFontStyle.REGULAR_ANYBODY_24,
      color: "#FFFFFF",
      textAlign: "center",
      marginBottom: 5,
    },
    summaryContainer: {
      backgroundColor: "#18151B",
      padding: 15,
      borderRadius: 10,
      width: "100%",
      marginVertical: 15,
    },
    summaryHeaderContainer: {
      backgroundColor: "#18151B",
      padding: 5,
      borderTopLeftRadius: 10,
      borderTopRightRadius: 10,
    },
    summaryHeader: {
      ...AppFontStyle.BOLD_ANYBODY_20,
      color: AppColors.white,
    },
    summaryRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 8,
      paddingTop: 10,
    },
    summaryBox: {
      backgroundColor: AppColors.purple_453B50,
      width: "19.3%",
      padding: 10,
      borderRadius: 8,
      marginBottom: 8,
    },
    summaryTitle: {
      ...AppFontStyle.BOLD_ANYBODY_14,
      color: AppColors.white,
      marginBottom: 5,
    },
    row: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
    },
    arrowIcon: {
      width: 12,
      height: 9.33,
      marginHorizontal: 5,
    },
    summaryValue: {
      ...AppFontStyle.BOLD_ANYBODY_12,
      color: "#B8B5C3",
      flexShrink: 1, // Allow text to shrink if needed
      flexWrap: "wrap",
      minWidth: 77,
    },
    buttonContainer: {
      flexDirection: "row",
      justifyContent: "space-between",
      width: "100%",
      marginTop: 10,
    },
    cancelButton: {
      backgroundColor: AppColors.white,
      borderRadius: 10,
      height: 38,
      width: "15%",
      shadowOpacity: 0,
      paddingVertical: 5,
    },
    cancelText: {
      color: AppColors.black,
      fontSize: 14,
    },
    submitButton: {
      backgroundColor: AppColors.buttonRed_DC2626,
      borderRadius: 10,
      height: 38,
      shadowOpacity: 0,
      paddingVertical: 0,
      paddingHorizontal: 10,
    },
    submitText: {
      color: AppColors.white,
      fontSize: 14,
    },
  },
  {
    [DEVICE_SIZES.MD]: { summaryBox: { width: "32.3%" } },
    [DEVICE_SIZES.LG]: { summaryBox: { width: "32.3%" } },
    [DEVICE_SIZES.SM]: {
      modalView: {
        width: "100%",
        backgroundColor: AppColors.black_121212,
        marginHorizontal: 0,
        alignSelf: "center",
        borderRadius: 12,
      },
      small: {
        backgroundColor: AppColors.purple_453B50,
        padding: 8,
        borderRadius: 24,
        marginBottom: 20,
        marginTop: 20,
        width: "100%",
      },
      header: {
        flexDirection: "row",
        width: "100%",
        justifyContent: "space-between",
        alignItems: "center",
      },
      headerText: {
        ...AppFontStyle.BOLD_ANYBODY_16,
        color: AppColors.white,
      },
      logo: {
        height: 80,
        width: 80,
        marginBottom: 10,
      },
      description: {
        ...AppFontStyle.REGULAR_ANYBODY_16,
        color: "#FFFFFF",
        textAlign: "center",
        marginBottom: 5,
      },
      summaryContainer: {
        backgroundColor: "#18151B",
        padding: 10,
        borderRadius: 10,
        width: "100%",
        marginBottom: 15,
      },
      summaryHeader: {
        ...AppFontStyle.BOLD_ANYBODY_16,
        color: AppColors.white,
      },
      summaryRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "space-between",
      },
      summaryBox: {
        width: "48%",
        marginBottom: 8,
      },
      summaryTitle: {
        display: "flex",
        justifyContent: "center",
      },
      row: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      },
      arrowIcon: {
        width: 12,
        height: 9.33,
        marginHorizontal: 5,
      },
      summaryValue: {
        ...AppFontStyle.BOLD_ANYBODY_12,
        color: "#B8B5C3",
        alignItems: "center",
        display: "flex",
        flexDirection: "row",
      },
      buttonContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        width: "100%",
      },
      cancelButton: {
        backgroundColor: AppColors.white,
        borderRadius: 10,
        height: 38,
        width: "22%",
        shadowOpacity: 0,
        paddingVertical: 5,
      },
      cancelText: {
        color: AppColors.black,
        fontSize: 12,
      },
      submitButton: {
        backgroundColor: AppColors.buttonRed_DC2626,
        borderRadius: 10,
        height: 38,
        shadowOpacity: 0,
      },
      submitText: {
        color: AppColors.white,
        fontSize: 12,
      },
    },
    [DEVICE_SIZES.XS]: {
      modalView: {
        width: "100%",
        backgroundColor: AppColors.black_121212,
        marginHorizontal: 0,
        alignSelf: "center",
        borderRadius: 12,
      },
      small: {
        backgroundColor: AppColors.purple_453B50,
        padding: 8,
        borderRadius: 24,
        marginBottom: 20,
        marginTop: 20,
        width: "100%",
      },
      content: {
        alignItems: "center",
        marginVertical: 0,
      },
      header: {
        flexDirection: "row",
        width: "100%",
        justifyContent: "space-between",
        alignItems: "center",
      },
      headerText: {
        ...AppFontStyle.BOLD_ANYBODY_16,
        color: AppColors.white,
      },
      logo: {
        height: 80,
        width: 80,
        marginBottom: 10,
      },
      description: {
        ...AppFontStyle.REGULAR_ANYBODY_16,
        color: "#FFFFFF",
        textAlign: "center",
        marginBottom: 5,
      },
      summaryContainer: {
        backgroundColor: "#18151B",
        padding: 10,
        borderRadius: 10,
        width: "100%",
        marginBottom: 15,
      },
      summaryHeader: {
        ...AppFontStyle.BOLD_ANYBODY_14,
        color: AppColors.white,
      },
      summaryRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 8,
        justifyContent: "space-between",
      },
      summaryBox: {
        width: "48%",
        marginBottom: 8,
      },
      summaryTitle: {
        display: "flex",
        justifyContent: "center",
      },
      row: {
        flexDirection: "row",
        justifyContent: "center",
        display: "flex",
      },
      arrowIcon: {
        width: 12,
        height: 9.33,
        marginHorizontal: 5,
      },
      summaryValue: {
        ...AppFontStyle.BOLD_ANYBODY_12,
        color: "#B8B5C3",
        alignItems: "center",
      },
      buttonContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        width: "100%",
      },
      cancelButton: {
        backgroundColor: AppColors.white,
        borderRadius: 10,
        height: 38,
        width: "22%",
        shadowOpacity: 0,
        paddingVertical: 5,
      },
      cancelText: {
        color: AppColors.black,
        fontSize: 12,
      },
      submitButton: {
        backgroundColor: AppColors.buttonRed_DC2626,
        borderRadius: 10,
        height: 38,
        shadowOpacity: 0,
      },
      submitText: {
        color: AppColors.white,
        fontSize: 12,
      },
    },
  }
);

export default ChangesReadyToSendModal;
